import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { useDemoMode } from '@/contexts/DemoModeContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Anchor, LogOut, Activity, Shield, Terminal,
  Radar, AlertTriangle, TrendingUp, Database,
  Play, RefreshCw, Zap, BarChart3,
  FileText, Crosshair, Download, MessageSquare,
  Send, Edit3, History, Bell, ZoomIn, ZoomOut,
  Upload, BookOpen, FileUp, Settings, Lightbulb,
  Users, UserPlus, Trash2, Save, Info, ExternalLink,
  FlaskConical, TrendingDown, Gauge, Clock, Eye,
  LayoutDashboard, FileSearch, Sliders, Globe, Languages,
  Brain, Layers, CheckCircle2, HelpCircle, Sparkles, LayoutGrid, Key, ListChecks, Skull, Map, Globe2
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import OnboardingTour from '@/components/OnboardingTour';
import SimulationPanel from '@/components/SimulationPanel';
import CrisisDetectionPanel from '@/components/CrisisDetectionPanel';
import TheaterSelector from '@/components/TheaterSelector';
import DocumentationPanel from '@/components/DocumentationPanel';
import TheaterComparisonPanel from '@/components/TheaterComparisonPanel';
import APIConfigPanel from '@/components/APIConfigPanel';
import SourcesConfigPanel from '@/components/SourcesConfigPanel';
import FeaturesPanel, { useFeatureAnnouncement } from '@/components/FeaturesPanel';
import GlobalConsole, { addGlobalLog } from '@/components/GlobalConsole';
import AdversaryIntelPanel from '@/components/AdversaryIntelPanel';
import SOCMINTPanel from '@/components/SOCMINTPanel';
import OsintPromptsPanel from '@/components/OsintPromptsPanel';
import TickerBanner from '@/components/TickerBanner';
import PremiumSourcesPanel from '@/components/PremiumSourcesPanel';
import AlertsPanel from '@/components/AlertsPanel';
import AlertsNotificationModal from '@/components/AlertsNotificationModal';
import AutoLearningBanner from '@/components/AutoLearningBanner';
import CoherenceAlertBanner from '@/components/CoherenceAlertBanner';
import AutoRefreshControl from '@/components/AutoRefreshControl';
import TheaterMap from '@/components/dashboard_tabs/TheaterMap';
import MarketsPanel from '@/components/dashboard_tabs/MarketsPanel';
import { LEDGauge, LEDBar, AlertBanner, VerboseLog, DashboardTab, ScanTab, ScenariosTab, ReportTab, HistoryTab, AdminUsersTab, AdminPanelTab, BriefTab } from '@/components/dashboard';

const APP_VERSION = "3.0";
const APP_NAME = "CESM OSINT SYSTEM";
const CENTER_NAME = "Centre d'Études Stratégiques de la Marine";

// ==================== MAIN DASHBOARD ====================
const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const { isDemoMode, checkDemoBlock, exitDemoMode } = useDemoMode();
  const navigate = useNavigate();
  
  // Font size state - DEFAULT TO LARGE
  const [fontSize, setFontSize] = useState('large'); // small, normal, large, xlarge
  
  // States
  const [kpiData, setKpiData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sources, setSources] = useState([]);
  const [scanLogs, setScanLogs] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [currentScanId, setCurrentScanId] = useState(null);
  const [activeTab, setActiveTab] = useState('brief');
  const [reports, setReports] = useState([]);
  
  // Brief states - PRE-FILLED with Admiral's original brief
  const [scanBrief, setScanBrief] = useState(`Mon centre d'études doit sortir ce soir une fiche visant à expliciter la conséquence pour la France de la fermeture du détroit d'Ormuz, en matière d'approvisionnement en hydrocarbures.

Nous travaillons ici sur plusieurs aspects :
- Le sujet des volumes entre pétrole brut, pétrole raffiné, gaz. Nous estimons que la France est peu exposée mais ce serait très bien de le démontrer avec des chiffres probants
- Le sujet prix, du raffiné comme du gaz, avec deux sous-sujets :
  • La hausse structurelle, qui découle de l'application d'un modèle économique offre/demande, liée à la simple question des volumes manquant à l'appel
  • Le risque de hausse passagère, liée à l'inquiétude des marchés, qui s'estime sans doute par comparaison avec des cas similaires dans le passé

INFORMATIONS COMPLÉMENTAIRES IMPORTANTES :
- Prendre en compte le flux hydrocarbure venant d'Amérique Latine comme source alternative
- Distinguer deux types de prix :
  • Prix de CRISE : lié à la panique des marchés (court terme, volatil)
  • Prix MACRO : lié au calcul économique réel. Si 20% du flux manque (en réalité ~10% grâce aux oléoducs vers la Mer Rouge), le prix du Brent n'a pas de raison économique de tripler sur le long terme
- Les USA ont la capacité de production pour calmer les prix à moyen terme`);
  const [briefQuestions, setBriefQuestions] = useState([]);
  const [briefSuggestions, setBriefSuggestions] = useState([]);
  const [briefAnswers, setBriefAnswers] = useState({});
  const [briefAnalyzed, setBriefAnalyzed] = useState(false);
  const [isAnalyzingBrief, setIsAnalyzingBrief] = useState(false);
  
  // Document references
  const [referenceDocuments, setReferenceDocuments] = useState([]);
  const [ancientReports, setAncientReports] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState(
    `Tu es un analyste stratégique du CESM (Centre d'Études Stratégiques de la Marine).
Ton rôle est d'analyser les données OSINT et de produire des rapports de renseignement.
Focus: analyse du présent immédiat et prédiction à court/moyen terme.
Tu dois identifier les signaux faibles et les tendances émergentes.`
  );
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  
  // Theater selection
  const [activeTheater, setActiveTheater] = useState('hormuz');
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showTheaterComparison, setShowTheaterComparison] = useState(false);
  const [showAPIConfig, setShowAPIConfig] = useState(false);
  const [aisStatus, setAisStatus] = useState(null);
  
  // Period states
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Report states
  const [currentReport, setCurrentReport] = useState(null);
  const [reportImprovements, setReportImprovements] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  
  // Alerts
  const [activeAlert, setActiveAlert] = useState(null);
  
  // Admin - User management states
  const ADMIN_RANKS = ["amiral", "vice-amiral", "contre-amiral", "capitaine_vaisseau"];
  const isAdmin = user && ADMIN_RANKS.includes(user.rank);
  const [adminUsers, setAdminUsers] = useState([]);
  const [availableRanks, setAvailableRanks] = useState([]);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', rank: 'aspirant', role: 'analyst' });
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Scenarios
  const [scenarios, setScenarios] = useState([]);
  
  // Premium Sources Panel
  const [showPremiumSources, setShowPremiumSources] = useState(false);
  
  // Alerts System
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [alertsSummary, setAlertsSummary] = useState({ unread_count: 0, critical_count: 0, high_count: 0, recent_critical: null });
  const [isGeneratingScenarios, setIsGeneratingScenarios] = useState(false);

  // Admin Dashboard states
  const [adminStats, setAdminStats] = useState(null);
  const [adminSessions, setAdminSessions] = useState([]);
  const [adminReports, setAdminReports] = useState([]);
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);

  // Scan Configuration states
  const [scanConfig, setScanConfig] = useState(null);
  const [selectedDepth, setSelectedDepth] = useState('standard');
  const [selectedAiLevel, setSelectedAiLevel] = useState('standard');
  const [selectedCategories, setSelectedCategories] = useState(['western', 'adversary', 'regional']);
  const [selectedLanguages, setSelectedLanguages] = useState(['en', 'fr']);
  const [showScanConfig, setShowScanConfig] = useState(false);

  // Sources Configuration Panel
  const [showSourcesConfig, setShowSourcesConfig] = useState(false);

  // Onboarding Tour state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Risk indices state (NEW)
  const [riskIndices, setRiskIndices] = useState(null);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [maritimeData, setMaritimeData] = useState(null);

  // Data coherence state (v2.6.1)
  const [coherenceRepairs, setCoherenceRepairs] = useState([]);

  // Auto-refresh KPI state (v2.6.1)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(60); // secondes
  const [lastKpiValues, setLastKpiValues] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Font size classes - MUCH BIGGER
  const fontSizeClasses = {
    small: 'text-base',
    normal: 'text-lg',
    large: 'text-xl',
    xlarge: 'text-2xl'
  };

  const baseFontSize = fontSizeClasses[fontSize] || 'text-sm';

  // Feature announcement on first login / new version
  const { showFeatures, setShowFeatures, showOnlyNew } = useFeatureAnnouncement(user);

  // Show onboarding on first login OR when app version changed
  useEffect(() => {
    if (!user) return;
    const lastVersion = user.last_tour_version;
    const needsTour = user.is_first_login || !lastVersion || lastVersion !== APP_VERSION;
    if (needsTour) {
      // Close features panel to avoid overlap, then show tour
      const timer = setTimeout(() => {
        setShowFeatures(false);
        setShowOnboarding(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`, { version: APP_VERSION }, authHeaders);
      toast.success('Tutoriel termine !');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  useEffect(() => {
    addGlobalLog('info', 'SYSTEM', 'Initialisation tableau de bord...');
    fetchKPIs();
    fetchSources();
    fetchLatestAnalysis();
    fetchReports();
    fetchRiskIndices();
    fetchLiveAlerts();
    fetchGeopoliticalData();
    fetchAISStatus();
    fetchAlertsSummary();
    addGlobalLog('success', 'SYSTEM', 'Chargement données en cours');
  }, []);

  // Auto-refresh KPI effect (v2.6.1)
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const interval = setInterval(() => {
      addGlobalLog('info', 'AUTO-REFRESH', `Actualisation KPI (toutes les ${autoRefreshInterval}s)`);
      fetchKPIs();
      fetchGeopoliticalData();
      fetchRiskIndices();
    }, autoRefreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [autoRefreshEnabled, autoRefreshInterval]);

  // Fetch Alerts Summary for notification
  const fetchAlertsSummary = async () => {
    try {
      const response = await axios.get(`${API}/alerts/summary`, authHeaders);
      if (response.data.status === 'success') {
        setAlertsSummary(response.data);
        // Ne montrer la modale d'alertes QUE si le FeaturesPanel n'est pas affiché
        // Sinon, le useEffect ci-dessous s'en chargera quand showFeatures passera à false
        if (response.data.unread_count > 0 && !showFeatures) {
          setTimeout(() => {
            setShowAlertsModal(true);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Error fetching alerts summary:', error);
    }
  };

  // Quand le FeaturesPanel se ferme, montrer les alertes si besoin (sauf si tutoriel actif)
  useEffect(() => {
    if (!showFeatures && !showOnboarding && alertsSummary?.unread_count > 0 && !showAlertsModal) {
      const timer = setTimeout(() => setShowAlertsModal(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showFeatures, showOnboarding]);

  // Fetch AIS Status for indicator
  const fetchAISStatus = async () => {
    try {
      const response = await axios.get(`${API}/ais/status`, authHeaders);
      setAisStatus(response.data.ais);
    } catch (error) {
      console.error('Error fetching AIS status:', error);
    }
  };

  useEffect(() => {
    let interval;
    if (isScanning && currentScanId) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/scan/${currentScanId}/logs`, authHeaders);
          setScanLogs(response.data.logs);
          if (response.data.status === 'completed' || response.data.status === 'error') {
            setIsScanning(false);
            if (response.data.status === 'completed') {
              setActiveAlert({ type: 'success', message: 'SCAN TERMINÉ - Génération du rapport...' });
              await generateAnalysis(currentScanId);
            }
          }
        } catch (error) { console.error('Error:', error); }
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isScanning, currentScanId]);

  const fetchKPIs = async () => {
    try {
      addGlobalLog('api', 'KPI', 'Récupération données KPI et OSINT...');
      // Fetch both old KPIs and new OSINT live data
      const [kpiResponse, osintResponse] = await Promise.all([
        axios.get(`${API}/kpi/latest`, authHeaders).catch(() => ({ data: {} })),
        axios.get(`${API}/osint/energy`, authHeaders).catch(() => ({ data: { data: {} } }))
      ]);
      
      const osintData = osintResponse.data?.data || {};
      
      // Merge old KPIs with live OSINT data (OSINT takes priority if available)
      setKpiData({
        ...kpiResponse.data,
        // Override with live data
        france_oil_dependency_ormuz: osintData.france_oil_dependency_ormuz || kpiResponse.data?.france_oil_dependency_ormuz,
        france_gas_dependency_ormuz: osintData.france_gas_dependency_ormuz || kpiResponse.data?.france_gas_dependency_ormuz,
        brent_price_current: osintData.brent_price_current || kpiResponse.data?.brent_price_current,
        brent_price_crisis_estimate: osintData.brent_price_crisis_estimate || kpiResponse.data?.brent_price_crisis_estimate,
        gas_price_current: osintData.gas_price_current || kpiResponse.data?.gas_price_current,
        gas_price_crisis_estimate: osintData.gas_price_crisis_estimate || kpiResponse.data?.gas_price_crisis_estimate,
        french_strategic_reserves_days: osintData.french_strategic_reserves_days || kpiResponse.data?.french_strategic_reserves_days,
        data_source: osintData.data_source || 'LEGACY',
        market_sentiment: osintData.market_sentiment,
        supply_disruption_risk: osintData.supply_disruption_risk
      });
      addGlobalLog('success', 'KPI', 'Données KPI chargées', osintData.data_source || 'LEGACY');
    } catch (error) { 
      addGlobalLog('error', 'KPI', 'Erreur récupération KPI');
      console.error('Error fetching KPIs:', error); 
    }
  };

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/sources`, authHeaders);
      setSources(response.data);
      addGlobalLog('data', 'SOURCES', `${response.data?.length || 0} sources chargées`);
    } catch (error) { console.error('Error:', error); }
  };

  const fetchLatestAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analyses`, authHeaders);
      if (response.data.length > 0) setAnalysis(response.data[0]);
    } catch (error) { console.error('Error:', error); }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, authHeaders);
      setReports(response.data);
      addGlobalLog('data', 'RAPPORT', `${response.data?.length || 0} rapports en historique`);
    } catch (error) { console.error('Error:', error); }
  };

  const fetchRiskIndices = async () => {
    try {
      addGlobalLog('api', 'RISK', 'Calcul indices de risque GSDI...');
      const response = await axios.get(`${API}/risk/gsdi`, authHeaders);
      setRiskIndices(response.data);
      addGlobalLog('success', 'RISK', 'Indices GSDI calculés');
    } catch (error) { 
      addGlobalLog('error', 'RISK', 'Erreur calcul indices');
      console.error('Error fetching risk indices:', error); 
    }
  };

  const [geopoliticalData, setGeopoliticalData] = useState(null);

  const fetchGeopoliticalData = async () => {
    try {
      addGlobalLog('api', 'GDELT', 'Récupération données géopolitiques...');
      const response = await axios.get(`${API}/osint/geopolitical`, authHeaders);
      setGeopoliticalData(response.data.data);
      const eventCount = response.data.data?.recent_events?.length || 0;
      
      // Vérifier si des réparations ont été effectuées (v2.6.1)
      if (response.data.data_repaired && response.data.data?._coherence_repairs) {
        const repairs = response.data.data._coherence_repairs;
        setCoherenceRepairs(repairs);
        addGlobalLog('warning', 'COHERENCE', `Auto-réparation: ${repairs.length} champ(s) corrigé(s)`);
        toast.warning(`Données anormales détectées et corrigées automatiquement`, {
          description: `${repairs.length} valeur(s) réparée(s) - Cliquez pour détails`
        });
      }
      
      addGlobalLog('success', 'GDELT', `${eventCount} événements géopolitiques`);
    } catch (error) { 
      addGlobalLog('error', 'GDELT', 'Erreur données géopolitiques');
      console.error('Error fetching geopolitical data:', error); 
    }
  };

  const fetchLiveAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`, authHeaders);
      setLiveAlerts(response.data.alerts || []);
    } catch (error) { console.error('Error fetching alerts:', error); }
  };

  const fetchMaritimeData = async () => {
    try {
      const response = await axios.get(`${API}/osint/maritime/hormuz`, authHeaders);
      setMaritimeData(response.data.data);
    } catch (error) { console.error('Error fetching maritime data:', error); }
  };

  const analyzeBrief = async () => {
    if (!scanBrief.trim()) {
      toast.error('Veuillez entrer un brief de mission');
      return;
    }
    
    setIsAnalyzingBrief(true);
    try {
      const response = await axios.post(`${API}/brief/analyze`, {
        brief: scanBrief,
        topic: 'Détroit d\'Ormuz - Hydrocarbures France'
      }, authHeaders);
      
      setBriefQuestions(response.data.questions);
      // Generate suggestions
      setBriefSuggestions([
        "Voulez-vous aussi une analyse des routes alternatives (Suez, Cap de Bonne-Espérance)?",
        "Je vous propose d'inclure une comparaison avec les crises précédentes (1973, 1990, 2022)?",
        "Vous serait-il utile d'avoir une projection à 3, 6 et 12 mois?",
        "Souhaitez-vous intégrer l'impact sur les pays partenaires européens?"
      ]);
      setBriefAnalyzed(true);
      setActiveAlert({ type: 'warning', message: 'ACTION REQUISE - Répondez aux questions pour affiner l\'analyse' });
      toast.success('Brief analysé');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
    }
    setIsAnalyzingBrief(false);
  };

  const startScan = async () => {
    // Bloquer en mode démo
    if (checkDemoBlock('Scan OSINT')) return;
    
    try {
      setIsScanning(true);
      setScanLogs([]);
      setActiveAlert({ type: 'info', message: 'SCAN OSINT EN COURS...' });
      addGlobalLog('progress', 'SCAN', 'Démarrage scan OSINT...');
      
      const fullBrief = `${scanBrief}\n\nRéponses:\n${Object.entries(briefAnswers).map(([q, a]) => `Q: ${q}\nR: ${a}`).join('\n')}\n\nDocuments de référence: ${referenceDocuments.length} fichiers\nRapports anciens: ${ancientReports.length} fichiers`;
      
      const response = await axios.post(`${API}/scan/start`, {
        topic: 'Détroit d\'Ormuz - Approvisionnement Hydrocarbures France',
        brief: fullBrief,
        period_start: periodStart,
        period_end: periodEnd,
        scan_depth: selectedDepth,
        ai_level: selectedAiLevel,
        categories: selectedCategories,
        languages: selectedLanguages
      }, authHeaders);
      
      setCurrentScanId(response.data.scan_id);
      setActiveTab('scan');
      addGlobalLog('success', 'SCAN', `Scan lancé: ${response.data.source_count} sources`);
      
      if (response.data.config) {
        toast.success(`Scan lancé: ${response.data.config.depth} | IA: ${response.data.config.ai_level} | ${response.data.source_count} sources`);
      }
    } catch (error) {
      setIsScanning(false);
      addGlobalLog('error', 'SCAN', 'Erreur lancement scan');
      toast.error(error.response?.data?.detail || 'Erreur lors du scan');
    }
  };

  // Load scan configuration
  const fetchScanConfig = async () => {
    try {
      const response = await axios.get(`${API}/scan/config`, authHeaders);
      setScanConfig(response.data);
    } catch (error) {
      console.error('Error loading scan config:', error);
    }
  };

  useEffect(() => {
    fetchScanConfig();
  }, [token]);

  const generateAnalysis = async (scanId) => {
    if (checkDemoBlock('Analyse IA')) return;
    try {
      const response = await axios.post(`${API}/analysis/generate`, {
        scan_id: scanId,
        analysis_type: 'full'
      }, authHeaders);
      
      setAnalysis(response.data);
      setKpiData(response.data.kpi_data);
      await generateReport(scanId, false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const generateReport = async (scanId, withImprovements = false) => {
    if (checkDemoBlock('Génération de rapport')) return;
    setIsGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/reports/generate`, {
        scan_id: scanId || currentScanId,
        brief: scanBrief,
        improvements: withImprovements ? reportImprovements : null,
        previous_version: withImprovements && currentReport ? currentReport.version : null
      }, authHeaders);
      
      setCurrentReport(response.data);
      setReportImprovements('');
      setActiveAlert({ type: 'success', message: `RAPPORT ${response.data.report_number} PRÊT!` });
      setActiveTab('report');
      fetchReports();
    } catch (error) {
      toast.error('Erreur');
    }
    setIsGeneratingReport(false);
  };

  const handleDocumentUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'reference') {
      setReferenceDocuments([...referenceDocuments, ...files.map(f => f.name)]);
    } else {
      setAncientReports([...ancientReports, ...files.map(f => f.name)]);
    }
    toast.success(`${files.length} document(s) ajouté(s)`);
  };

  // Admin functions
  const fetchAdminUsers = async () => {
    if (!isAdmin) return;
    setIsLoadingUsers(true);
    try {
      const [usersRes, ranksRes] = await Promise.all([
        axios.get(`${API}/admin/users`, authHeaders),
        axios.get(`${API}/admin/ranks`, authHeaders)
      ]);
      setAdminUsers(usersRes.data);
      setAvailableRanks(ranksRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
    setIsLoadingUsers(false);
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Tous les champs sont requis');
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, newUser, authHeaders);
      toast.success('Utilisateur créé');
      setShowNewUserForm(false);
      setNewUser({ email: '', password: '', name: '', rank: 'aspirant', role: 'analyst' });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Confirmer la suppression de cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authHeaders);
      toast.success('Utilisateur supprimé');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole }, authHeaders);
      toast.success('Rôle mis à jour');
      fetchAdminUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const exportPDF = async (reportId) => {
    try {
      toast.info('Génération du PDF en cours...');
      const response = await axios.get(`${API}/reports/${reportId}/pdf`, {
        ...authHeaders,
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-cesm-${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erreur export PDF');
    }
  };

  // Generate scenarios - works with or without scan
  const generateScenarios = async () => {
    setIsGeneratingScenarios(true);
    try {
      // If no scan exists, create one first
      if (!currentScanId && scanBrief.trim()) {
        toast.info('Lancement du scan OSINT avant génération des scénarios...');
        await startScan();
        // Wait a bit for scan to complete
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      const scanId = currentScanId || `manual-${Date.now()}`;
      const response = await axios.post(`${API}/scenarios/generate`, {
        scan_id: scanId,
        base_kpi: kpiData
      }, authHeaders);
      setScenarios(response.data.scenarios);
      toast.success('4 scénarios générés');
    } catch (error) {
      console.error('Scenario generation error:', error);
      toast.error('Erreur génération scénarios');
    }
    setIsGeneratingScenarios(false);
  };

  // Fetch existing scenarios
  const fetchScenarios = async (scanId) => {
    try {
      const response = await axios.get(`${API}/scenarios/${scanId}`, authHeaders);
      if (response.data.scenarios) {
        setScenarios(response.data.scenarios);
      }
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    }
  };

  // Load scenarios when scan changes
  useEffect(() => {
    if (currentScanId) {
      fetchScenarios(currentScanId);
    }
  }, [currentScanId]);

  // Load admin data when tab changes
  useEffect(() => {
    if (activeTab === 'admin' && isAdmin) {
      fetchAdminUsers();
    }
    if (activeTab === 'adminpanel' && isAdmin) {
      fetchAdminDashboard();
    }
  }, [activeTab, isAdmin]);

  // Load admin dashboard data
  const fetchAdminDashboard = async () => {
    if (!isAdmin) return;
    setIsLoadingAdmin(true);
    try {
      const [statsRes, sessionsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/admin/sessions`, authHeaders),
        axios.get(`${API}/admin/reports/all`, authHeaders)
      ]);
      setAdminStats(statsRes.data);
      setAdminSessions(sessionsRes.data?.sessions || sessionsRes.data || []);
      setAdminReports(reportsRes.data?.reports || reportsRes.data || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
    setIsLoadingAdmin(false);
  };

  // Send heartbeat to track active sessions
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/admin/sessions/heartbeat`, {}, authHeaders);
      } catch (error) {
        // Silent fail
      }
    };
    
    // Send heartbeat every 5 minutes
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    // En mode démo, simplement quitter le mode démo
    if (isDemoMode) {
      exitDemoMode();
      navigate('/login');
      return;
    }
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen bg-marine-void ${baseFontSize} overflow-x-hidden`} data-testid="dashboard">
      {/* Bannière Mode Démo */}
      {isDemoMode && (
        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b-2 border-amber-500 px-4 py-2 flex items-center justify-between sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-amber-400 animate-pulse" />
            <div>
              <span className="font-heading text-amber-400 text-sm uppercase tracking-wider">MODE DÉMONSTRATION</span>
              <span className="font-mono text-white/70 text-xs ml-3">Visualisation uniquement - Actions désactivées</span>
            </div>
          </div>
          <button 
            onClick={exitDemoMode}
            className="px-3 py-1 bg-amber-500/20 border border-amber-500 text-amber-400 text-xs font-mono hover:bg-amber-500/40 transition-all"
          >
            QUITTER LE MODE DÉMO
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="border-b-2 border-marine-slate/50 bg-black sticky top-0 z-40 w-full">
        <div className="flex items-center justify-between px-2 md:px-4 py-2 max-w-full">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Anchor className="w-6 h-6 text-marine-cyan flex-shrink-0" />
            <div className="hidden sm:block">
              <span className="font-heading text-marine-cyan text-sm md:text-lg tracking-widest block">CESM</span>
              <span className="font-mono text-marine-amber text-xs">v{APP_VERSION}</span>
            </div>
          </div>
          
          {/* Right side - minimal on mobile */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={() => {
                if (!scanBrief.trim()) {
                  toast.error('Entrez d\'abord un brief de mission');
                  setActiveTab('brief');
                  return;
                }
                startScan();
              }}
              disabled={isScanning}
              className="p-2 bg-marine-green/20 border border-marine-green text-marine-green"
              data-testid="quick-scan-btn"
            >
              {isScanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setShowOnboarding(true)}
              className="p-2 text-marine-amber/80 hover:text-marine-amber hover:bg-marine-amber/10 transition-colors"
              title="Tutoriel interactif"
              data-testid="tutorial-btn"
            >
              <BookOpen className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} className="p-2 text-white/80 hover:text-red-400">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation - scrollable on mobile */}
      <nav className="bg-black/50 border-b border-marine-slate/30 flex items-center">
        <div className="flex items-center gap-0 px-2 min-w-0 overflow-x-auto flex-1">
          {[
            { id: 'brief', label: 'Brief', icon: FileText },
            { id: 'scan', label: 'Scan', icon: Radar },
            { id: 'adversary', label: 'Adversaires', icon: Skull },
            { id: 'socmint', label: 'SOCMINT', icon: Globe },
            { id: 'osint_prompts', label: 'Prompts', icon: Zap },
            { id: 'carte', label: 'Carte', icon: Map },
            { id: 'marches', label: 'Marches', icon: TrendingUp },
            { id: 'alerts', label: 'Alertes', icon: Bell, badge: alertsSummary.unread_count },
            { id: 'dashboard', label: 'Dashboard', icon: Activity },
            { id: 'crisis', label: 'Alerte', icon: Shield },
            { id: 'simulation', label: 'Simu', icon: Sparkles },
            { id: 'scenarios', label: 'Scénarios', icon: FlaskConical },
            { id: 'report', label: 'Rapport', icon: BarChart3 },
            { id: 'history', label: 'Histo', icon: History },
            { id: 'worldmonitor', label: 'WorldMon', icon: Globe2 },
            ...(isAdmin ? [
              { id: 'admin', label: 'Users', icon: Users },
              { id: 'adminpanel', label: 'Admin', icon: LayoutDashboard }
            ] : []),
            { id: 'docs', label: 'Docs', icon: FileSearch, isLink: true, href: '/api/static/AUDIT_TECHNIQUE.md' },
          ].map(tab => (
            tab.isLink ? (
              <a
                key={tab.id}
                href={tab.href}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 font-mono text-xs uppercase tracking-wider flex items-center gap-1 border-b-2 whitespace-nowrap text-purple-400 border-transparent hover:bg-purple-900/20"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </a>
            ) : (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 font-mono text-xs uppercase tracking-wider flex items-center gap-1 border-b-2 whitespace-nowrap relative ${
                activeTab === tab.id 
                  ? 'bg-marine-cyan/20 text-marine-cyan border-marine-cyan font-bold' 
                  : 'text-white/90 border-transparent'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
            )
          ))}
        </div>
          {/* Bouton Fonctions - toujours visible */}
          <button
            onClick={() => setShowFeatures(true)}
            className="px-3 py-2 font-mono text-xs uppercase tracking-wider flex items-center gap-1 text-marine-amber hover:bg-marine-amber/20 whitespace-nowrap flex-shrink-0 border-l border-marine-slate/30"
            data-testid="features-btn"
          >
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Fonctions</span>
          </button>
      </nav>

      {/* Ticker Banner - Sources Recommandées */}
      <TickerBanner onSourceClick={() => setShowPremiumSources(true)} />

      {/* Coherence Alert Banner - Auto-réparation des données */}
      {coherenceRepairs.length > 0 && (
        <div className="px-4 pt-4">
          <CoherenceAlertBanner 
            repairs={coherenceRepairs}
            onDismiss={() => setCoherenceRepairs([])}
            onRetry={() => {
              setCoherenceRepairs([]);
              fetchGeopoliticalData();
            }}
          />
        </div>
      )}

      {/* Alert Banner */}
      {activeAlert && (
        <div className="px-4 py-2">
          <AlertBanner 
            type={activeAlert.type} 
            message={activeAlert.message}
            action={activeAlert.type === 'warning' ? 'RÉPONDRE' : null}
            onAction={() => setActiveTab('brief')}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="p-4 md:p-6">
        
        {/* BRIEF TAB */}
        {activeTab === 'brief' && (
          <BriefTab
            token={token}
            activeTheater={activeTheater}
            setActiveTheater={setActiveTheater}
            scanBrief={scanBrief}
            setScanBrief={setScanBrief}
            systemPrompt={systemPrompt}
            setSystemPrompt={setSystemPrompt}
            showPromptEditor={showPromptEditor}
            setShowPromptEditor={setShowPromptEditor}
            periodStart={periodStart}
            setPeriodStart={setPeriodStart}
            periodEnd={periodEnd}
            setPeriodEnd={setPeriodEnd}
            ancientReports={ancientReports}
            referenceDocuments={referenceDocuments}
            handleDocumentUpload={handleDocumentUpload}
            briefAnalyzed={briefAnalyzed}
            setBriefAnalyzed={setBriefAnalyzed}
            isAnalyzingBrief={isAnalyzingBrief}
            analyzeBrief={analyzeBrief}
            briefSuggestions={briefSuggestions}
            briefQuestions={briefQuestions}
            briefAnswers={briefAnswers}
            setBriefAnswers={setBriefAnswers}
            isScanning={isScanning}
            startScan={startScan}
            showScanConfig={showScanConfig}
            setShowScanConfig={setShowScanConfig}
            scanConfig={scanConfig}
            selectedDepth={selectedDepth}
            setSelectedDepth={setSelectedDepth}
            selectedAiLevel={selectedAiLevel}
            setSelectedAiLevel={setSelectedAiLevel}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
          />
        )}

        {/* SCAN TAB */}
        {activeTab === 'scan' && (
          <ScanTab 
            scanLogs={scanLogs} 
            isScanning={isScanning} 
            sources={sources}
            token={token}
          />
        )}

        {/* ADVERSARY INTEL TAB */}
        {activeTab === 'adversary' && (
          <AdversaryIntelPanel token={token} />
        )}

        {/* SOCMINT TAB */}
        {activeTab === 'socmint' && (
          <SOCMINTPanel token={token} />
        )}

        {/* OSINT PROMPTS TAB */}
        {activeTab === 'osint_prompts' && (
          <OsintPromptsPanel token={token} />
        )}

        {/* CARTE / THEATER MAP TAB */}
        {activeTab === 'carte' && (
          <div className="h-[calc(100vh-120px)]">
            <TheaterMap token={token} currentTheater={activeTheater} />
          </div>
        )}

        {/* MARCHES TAB */}
        {activeTab === 'marches' && (
          <MarketsPanel token={token} />
        )}

        {/* ALERTS TAB */}
        {activeTab === 'alerts' && (
          <AlertsPanel token={token} />
        )}

        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <>
            {/* Auto-refresh control bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="font-mono text-white/80 text-xs uppercase">Actualisation données</span>
              </div>
              <AutoRefreshControl
                enabled={autoRefreshEnabled}
                onToggle={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                interval={autoRefreshInterval}
                onIntervalChange={setAutoRefreshInterval}
              />
            </div>
            <DashboardTab 
              kpiData={kpiData}
              riskIndices={riskIndices}
              geopoliticalData={geopoliticalData}
              analysis={analysis}
              fontSize={fontSize}
            />
          </>
        )}

        {/* CRISIS DETECTION TAB */}
        {activeTab === 'crisis' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider flex items-center gap-3">
                <Shield className="w-7 h-7" />
                Système d'Alerte Précoce
              </h2>
            </div>
            <CrisisDetectionPanel token={token} />
          </div>
        )}

        {/* SIMULATION TAB */}
        {activeTab === 'simulation' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider flex items-center gap-3">
                <Sparkles className="w-7 h-7" />
                Simulations Monte Carlo
              </h2>
            </div>
            {/* EXPLICATION DÉTAILLÉE - SIMU vs SCÉNARIOS */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-mono text-purple-400 font-bold text-sm mb-2">
                    SIMU vs SCÉNARIOS : Quelle différence ?
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-xs font-mono">
                    <div className="bg-black/30 p-3 rounded border border-purple-500/20">
                      <p className="text-purple-400 font-bold mb-1">SIMU (cet onglet)</p>
                      <ul className="text-white/90 space-y-1">
                        <li>• <strong className="text-white">Simulations Monte Carlo</strong> (500+ itérations)</li>
                        <li>• Sélectionnez un <strong className="text-white">template de crise</strong> prédéfini</li>
                        <li>• Résultats <strong className="text-white">quantitatifs précis</strong> : P10/P50/P90</li>
                        <li>• Impact chiffré : prix Brent, TTF, coût France</li>
                        <li>• Calibré sur données historiques (1973, 1990, 2022...)</li>
                      </ul>
                    </div>
                    <div className="bg-black/30 p-3 rounded border border-cyan-500/20">
                      <p className="text-cyan-400 font-bold mb-1">SCÉNARIOS (autre onglet)</p>
                      <ul className="text-white/90 space-y-1">
                        <li>• Génère <strong className="text-white">4 hypothèses narratives</strong></li>
                        <li>• Basé sur <strong className="text-white">votre brief personnel</strong></li>
                        <li>• Analyse <strong className="text-white">qualitative</strong> avec probabilités</li>
                        <li>• Types: Favorable / Médian / Défavorable / Catastrophe</li>
                        <li>• Inclut déclencheurs contextuels</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-marine-slate/30 border border-marine-cyan/30 rounded-lg p-4 text-sm text-slate-300">
              <p className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-marine-cyan mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-white">Mode d'emploi :</strong> Sélectionnez un template de crise ci-dessous 
                  (ex: "Blocus du détroit d'Ormuz"), puis lancez la simulation. Le système effectuera 500+ itérations 
                  Monte Carlo pour calculer l'impact probable sur les prix et les réserves.
                </span>
              </p>
            </div>
            <SimulationPanel token={token} />
          </div>
        )}

        {/* SCENARIOS TAB */}
        {activeTab === 'scenarios' && (
          <ScenariosTab 
            scenarios={scenarios}
            isGeneratingScenarios={isGeneratingScenarios}
            generateScenarios={generateScenarios}
          />
        )}

        {/* REPORT TAB */}
        {activeTab === 'report' && (
          <ReportTab 
            token={token}
            user={user}
            addGlobalLog={addGlobalLog}
            currentReport={currentReport}
            currentScanId={currentScanId}
            isGeneratingReport={isGeneratingReport}
            generateReport={generateReport}
          />
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <HistoryTab 
            token={token}
            reports={reports}
            setCurrentReport={setCurrentReport}
            setActiveTab={setActiveTab}
            exportPDF={exportPDF}
          />
        )}

        {/* WORLDMONITOR TAB */}
        {activeTab === 'worldmonitor' && (
          <div className="flex flex-col items-center justify-center py-16 space-y-6" data-testid="worldmonitor-tab">
            <Globe2 className="w-16 h-16 text-marine-cyan" />
            <h2 className="font-mono text-marine-cyan text-xl font-bold uppercase tracking-wider">
              WorldMonitor
            </h2>
            <p className="font-mono text-white/50 text-sm text-center max-w-lg">
              Dashboard de renseignement global en temps reel (46K+ stars GitHub).<br />
              Carte deck.gl, alertes missiles, jamming GPS, flux live, marches de prediction.
            </p>
            <div className="flex gap-3">
              <a href="https://worldmonitor.app" target="_blank" rel="noopener noreferrer"
                className="px-6 py-3 bg-marine-cyan/20 border-2 border-marine-cyan text-marine-cyan font-mono text-sm font-bold rounded flex items-center gap-2 hover:bg-marine-cyan/30 transition-all"
                data-testid="worldmon-open">
                <ExternalLink className="w-4 h-4" /> Ouvrir WorldMonitor
              </a>
              <a href="https://github.com/koala73/worldmonitor" target="_blank" rel="noopener noreferrer"
                className="px-6 py-3 bg-white/5 border border-white/20 text-white/60 font-mono text-sm rounded flex items-center gap-2 hover:text-white transition-all"
                data-testid="worldmon-github">
                Code Source (GitHub)
              </a>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl w-full">
              {[
                { label: 'Carte Mondiale', desc: 'deck.gl 45 couches' },
                { label: 'Alertes Missiles', desc: 'Iran/Israel temps reel' },
                { label: 'GPS Jamming', desc: 'Carte GNSS mondiale' },
                { label: 'Flux Live', desc: 'CNN, RT, Fox News' },
                { label: 'Polymarket', desc: 'Predictions conflits' },
                { label: 'CII Score', desc: 'Index intelligence pays' },
                { label: 'OREF Sirens', desc: 'Alertes roquettes Israel' },
                { label: 'NOTAM/Vols', desc: 'Restrictions aeriennes' },
              ].map((f, i) => (
                <div key={i} className="bg-black/40 border border-white/10 rounded p-3 text-center">
                  <p className="font-mono text-white/80 text-xs font-bold">{f.label}</p>
                  <p className="font-mono text-white/40 text-[10px] mt-1">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADMIN TAB - User Management */}
        {activeTab === 'admin' && isAdmin && (
          <AdminUsersTab 
            adminUsers={adminUsers}
            isLoadingUsers={isLoadingUsers}
            showNewUserForm={showNewUserForm}
            setShowNewUserForm={setShowNewUserForm}
            newUser={newUser}
            setNewUser={setNewUser}
            availableRanks={availableRanks}
            createUser={createUser}
            updateUserRole={updateUserRole}
            deleteUser={deleteUser}
            currentUserId={user?.id}
          />
        )}

        {/* ADMIN PANEL TAB */}
        {activeTab === 'adminpanel' && isAdmin && (
          <AdminPanelTab
            isLoadingAdmin={isLoadingAdmin}
            adminStats={adminStats}
            adminSessions={adminSessions}
            adminReports={adminReports}
            fetchAdminDashboard={fetchAdminDashboard}
            exportPDF={exportPDF}
            token={token}
            user={user}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-marine-slate/30 p-4 text-center bg-black/50">
        <p className="font-mono text-white/80 text-sm">
          {CENTER_NAME} | {APP_NAME} v{APP_VERSION} | CDT Jean-Baptiste Giraud (r)
        </p>
      </footer>

      {/* Onboarding Tour */}
      <OnboardingTour 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={completeOnboarding}
        isAdmin={isAdmin}
        currentVersion={APP_VERSION}
        lastTourVersion={user?.last_tour_version || ''}
      />
      
      {/* Documentation Panel */}
      <DocumentationPanel 
        token={token}
        isOpen={showDocumentation}
        onClose={() => setShowDocumentation(false)}
      />
      
      {/* Theater Comparison Panel */}
      <TheaterComparisonPanel
        token={token}
        isOpen={showTheaterComparison}
        onClose={() => setShowTheaterComparison(false)}
      />
      
      {/* API Configuration Panel */}
      <APIConfigPanel
        token={token}
        isOpen={showAPIConfig}
        onClose={() => setShowAPIConfig(false)}
      />
      
      {/* Sources Configuration Panel */}
      <SourcesConfigPanel
        token={token}
        isOpen={showSourcesConfig}
        onClose={() => setShowSourcesConfig(false)}
      />
      
      {/* Premium Sources Panel */}
      <PremiumSourcesPanel
        token={token}
        isOpen={showPremiumSources}
        onClose={() => setShowPremiumSources(false)}
      />
      
      {/* Alerts Notification Modal */}
      <AlertsNotificationModal
        isOpen={showAlertsModal}
        onClose={() => setShowAlertsModal(false)}
        onViewAlerts={() => {
          setShowAlertsModal(false);
          setActiveTab('alerts');
        }}
        unreadCount={alertsSummary.unread_count}
        criticalCount={alertsSummary.critical_count}
        highCount={alertsSummary.high_count}
        recentCritical={alertsSummary.recent_critical}
      />
      
      {/* Features Panel */}
      <FeaturesPanel
        isOpen={showFeatures}
        onClose={() => setShowFeatures(false)}
        showOnlyNew={showOnlyNew}
      />
      
      {/* Auto-Learning Banner (v2.6.0) - Affichage une fois par session */}
      <AutoLearningBanner
        token={token}
        isActive={isScanning || isGeneratingReport}
        scanTopic={scanBrief?.substring(0, 200) || 'analyse OSINT'}
        context={isScanning ? 'scan' : 'magic_report'}
        onLearnClick={(prompt) => {
          // Rediriger vers l'onglet admin si admin
          if (isAdmin) {
            setActiveTab('adminpanel');
            toast.info('Utilisez le panneau Apprentissage pour enrichir la doctrine');
          }
        }}
      />
      
      {/* Global Console */}
      <GlobalConsole />
    </div>
  );
};

export default Dashboard;
